import { ArrowDown, RotateCcw } from "lucide-react";

const START_STEP = { n: 1, label: "Strategieentwicklung" };
const CYCLE_STEPS = [
  { n: 2, label: "Trade" },
  { n: 3, label: "Journal" },
  { n: 4, label: "Analyse" },
  { n: 5, label: "Reflexion" },
  { n: 6, label: "Strategieanpassung" },
];

const CX = 280;
const CY_CYCLE = 360;
const R = 160;
const NODE_R = 32;
const LABEL_R = 225;
const NODE_EDGE_GAP = 6;

const START_POS = { x: CX, y: 70 };

function pointOnCircle(radius, angleDeg) {
  const rad = (angleDeg * Math.PI) / 180;
  return {
    x: CX + radius * Math.cos(rad),
    y: CY_CYCLE + radius * Math.sin(rad),
  };
}

function cycleAngle(i) {
  // 5 Knoten gleichmäßig verteilt, Start oben (Node 2), im Uhrzeigersinn
  return -90 + i * 72;
}

function arrowBetween(from, to) {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.sqrt(dx * dx + dy * dy);
  const ux = dx / len;
  const uy = dy / len;
  const offset = NODE_R + NODE_EDGE_GAP;
  return {
    x1: from.x + ux * offset,
    y1: from.y + uy * offset,
    x2: to.x - ux * offset,
    y2: to.y - uy * offset,
  };
}

export default function LearningCycle() {
  const cycleNodes = CYCLE_STEPS.map((step, i) => {
    const angle = cycleAngle(i);
    const pos = pointOnCircle(R, angle);
    // Label-Positionierung: Kollisionen mit Startknoten oben und Zyklus-Innenraum vermeiden.
    let labelX, labelY, align;
    if (i === 0) {
      // Node 2 (oben) — Label rechts neben dem Knoten
      labelX = pos.x + NODE_R + 12;
      labelY = pos.y - 9;
      align = "left";
    } else if (i === 4) {
      // Node 6 (oben links) — Label rechtsbündig links vom Knoten, damit es nicht in den Kreis ragt
      labelX = pos.x - NODE_R - 12;
      labelY = pos.y - 9;
      align = "right";
    } else {
      const labelPos = pointOnCircle(LABEL_R, angle);
      labelX = labelPos.x;
      labelY = labelPos.y;
      align = "center";
    }
    return {
      ...step,
      angle,
      x: pos.x,
      y: pos.y,
      labelX,
      labelY,
      align,
    };
  });

  const startNode = { ...START_STEP, x: START_POS.x, y: START_POS.y };

  // Pfeile: 1 → 2 (Start-Impuls) und Zyklus 2→3→4→5→6→2
  const arrows = [
    { ...arrowBetween(startNode, cycleNodes[0]), key: "start", label: "1→2" },
    ...cycleNodes.map((from, i) => {
      const to = cycleNodes[(i + 1) % cycleNodes.length];
      return { ...arrowBetween(from, to), key: `cycle-${i}`, label: `${from.n}→${to.n}` };
    }),
  ];

  return (
    <>
      {/* Desktop: Kreis mit Start-Knoten */}
      <div className="hidden md:block" data-testid="learning-cycle-svg">
        <svg
          viewBox="-40 0 640 610"
          className="w-full max-w-[560px] mx-auto"
          role="img"
          aria-label="Lernkreislauf: Startphase Strategieentwicklung, danach kontinuierlicher Zyklus Trade, Journal, Analyse, Reflexion, Strategieanpassung"
        >
          <defs>
            <marker
              id="lc-arrowhead"
              markerWidth="12"
              markerHeight="12"
              refX="10"
              refY="6"
              orient="auto"
              markerUnits="strokeWidth"
            >
              <path d="M0,0 L10,6 L0,12 Z" fill="hsl(var(--primary))" />
            </marker>
          </defs>

          {/* Guide-Kreis */}
          <circle
            cx={CX}
            cy={CY_CYCLE}
            r={R}
            fill="none"
            stroke="hsl(var(--border))"
            strokeDasharray="3 6"
            strokeOpacity="0.5"
          />

          {/* Pfeile */}
          {arrows.map((a, i) => (
            <line
              key={a.key}
              x1={a.x1}
              y1={a.y1}
              x2={a.x2}
              y2={a.y2}
              stroke="hsl(var(--primary))"
              strokeOpacity="0.9"
              strokeWidth="2"
              markerEnd="url(#lc-arrowhead)"
              data-testid={`learning-cycle-arrow-${a.label}`}
            />
          ))}

          {/* Zentrum */}
          <foreignObject x={CX - 90} y={CY_CYCLE - 32} width="180" height="72">
            <div
              xmlns="http://www.w3.org/1999/xhtml"
              style={{ textAlign: "center" }}
            >
              <div
                style={{
                  fontSize: "10px",
                  textTransform: "uppercase",
                  letterSpacing: "0.14em",
                  color: "hsl(var(--muted-foreground))",
                }}
              >
                Ziel
              </div>
              <div
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: "20px",
                  color: "hsl(var(--foreground))",
                  marginTop: "4px",
                  letterSpacing: "-0.01em",
                }}
              >
                Bessere Ergebnisse
              </div>
            </div>
          </foreignObject>

          {/* Start-Knoten (1) */}
          <g data-testid="learning-cycle-node-1">
            <circle
              cx={startNode.x}
              cy={startNode.y}
              r={NODE_R}
              fill="hsl(var(--primary) / 0.28)"
              stroke="hsl(var(--primary))"
              strokeOpacity="1"
              strokeWidth="2"
            />
            <text
              x={startNode.x}
              y={startNode.y + 6}
              textAnchor="middle"
              fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
              fontSize="18"
              fill="hsl(var(--foreground))"
              fontWeight="600"
            >
              1
            </text>
            <foreignObject
              x={startNode.x - 100}
              y={startNode.y - NODE_R - 34}
              width="200"
              height="30"
            >
              <div
                xmlns="http://www.w3.org/1999/xhtml"
                style={{
                  textAlign: "center",
                  fontSize: "13px",
                  color: "hsl(var(--foreground))",
                  fontWeight: 500,
                }}
              >
                {startNode.label}
              </div>
            </foreignObject>
          </g>

          {/* Zyklus-Knoten (2–6) */}
          {cycleNodes.map((node) => (
            <g key={node.n} data-testid={`learning-cycle-node-${node.n}`}>
              <circle
                cx={node.x}
                cy={node.y}
                r={NODE_R}
                fill="hsl(var(--primary) / 0.18)"
                stroke="hsl(var(--primary))"
                strokeOpacity="0.9"
                strokeWidth="1.75"
              />
              <text
                x={node.x}
                y={node.y + 6}
                textAnchor="middle"
                fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
                fontSize="18"
                fill="hsl(var(--foreground))"
                fontWeight="600"
              >
                {node.n}
              </text>
              <foreignObject
                x={
                  node.align === "left"
                    ? node.labelX
                    : node.align === "right"
                    ? node.labelX - 180
                    : node.labelX - 90
                }
                y={node.labelY - 18}
                width="180"
                height="40"
              >
                <div
                  xmlns="http://www.w3.org/1999/xhtml"
                  style={{
                    textAlign: node.align || "center",
                    fontSize: "13px",
                    lineHeight: 1.25,
                    color: "hsl(var(--foreground))",
                    fontWeight: 500,
                  }}
                >
                  {node.label}
                </div>
              </foreignObject>
            </g>
          ))}
        </svg>
      </div>

      {/* Mobile: Startpunkt + vertikale Kette 2–6, dann Loop-Hinweis */}
      <div className="md:hidden" data-testid="learning-cycle-list">
        <div
          className="flex items-center gap-3 rounded-xl border border-primary/50 bg-primary/15 px-4 py-3"
          data-testid="learning-cycle-item-1"
        >
          <span className="h-7 w-7 rounded-full border border-primary/60 bg-primary/25 grid place-items-center font-mono text-xs text-foreground shrink-0">
            {startNode.n}
          </span>
          <div className="flex-1">
            <div className="text-base font-medium leading-tight">
              {startNode.label}
            </div>
          </div>
        </div>
        <div className="flex justify-center py-1.5 text-primary-foreground/70">
          <ArrowDown className="h-4 w-4" strokeWidth={1.5} />
        </div>

        <div className="rounded-2xl border border-dashed border-border p-3 space-y-1">
          <div className="overline text-center pt-1 pb-2">Zyklus</div>
          {cycleNodes.map((n, i) => (
            <div key={n.n}>
              <div
                className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3"
                data-testid={`learning-cycle-item-${n.n}`}
              >
                <span className="h-7 w-7 rounded-full border border-primary/50 bg-primary/10 grid place-items-center font-mono text-xs text-foreground shrink-0">
                  {n.n}
                </span>
                <span className="text-base font-medium">{n.label}</span>
              </div>
              {i < cycleNodes.length - 1 && (
                <div className="flex justify-center py-1.5 text-primary-foreground/60">
                  <ArrowDown className="h-4 w-4" strokeWidth={1.5} />
                </div>
              )}
            </div>
          ))}
          <div className="flex items-center justify-center gap-2 pt-3 pb-1 text-xs text-muted-foreground italic">
            <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.5} />
            zurück zu Schritt 2 — der Zyklus wiederholt sich
          </div>
        </div>

        <div className="flex flex-col items-center gap-2 pt-4">
          <div className="rounded-xl border border-primary/40 bg-primary/10 px-5 py-3 text-center">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Ziel
            </div>
            <div className="font-heading text-lg mt-0.5">Bessere Ergebnisse</div>
          </div>
        </div>
      </div>
    </>
  );
}
